import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Grid, Stack } from "@mantine/core";
import { useInsertMutation } from "@supabase-cache-helpers/postgrest-react-query";
import { ErrorAlert } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import z from "zod";

import { api } from "@/api.ts";
import AuthorizationConfirmButton from "@/components/authorization/AuthorizationConfirmButton.tsx";
import AuthorizationDatasetSelector from "@/components/authorization/AuthorizationDatasetSelector.tsx";
import AuthorizationResponsibilityBoxes from "@/components/authorization/AuthorizationResponsibilityBoxes.tsx";
import {
  AuthorizationContext,
  useAuthorizeController,
} from "@/hooks/useAuthorizeController.tsx";

const searchParamsSchema = z.object({
  datasets: z.array(z.string()),
  geometry: z.string(),
  format: z.string(),
});

export const Route = createFileRoute("/flows/download/authorize/$identifier")({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
  head: () => ({
    meta: [
      {
        title:
          globalT`Sweco REALity` +
          " - " +
          globalT`Download` +
          " - " +
          globalT`Authorize`,
      },
    ],
  }),
});

function RouteComponent() {
  const search = Route.useSearch();

  const authorization = useAuthorizeController(
    Route.useParams().identifier,
    search.datasets,
  );

  const navigate = Route.useNavigate();
  const { mutateAsync: insertDownload } = useInsertMutation(
    api.from("download"),
    ["id"],
    "id",
  );
  const wasSubmittedRef = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const startDownload = useCallback(async () => {
    if (!authorization.project || wasSubmittedRef.current) {
      return;
    }
    wasSubmittedRef.current = true;
    try {
      const download = await insertDownload([
        {
          project_id: authorization.project.id,
          datasets: search.datasets,
          geometry: search.geometry,
          format: search.format,
        },
      ]);
      if (download && download.length > 0) {
        await navigate({
          to: "/flows/download/wait/$id",
          params: { id: download[0].id },
        });
      }
    } catch (error) {
      setSubmitError((error as Error).message);
      wasSubmittedRef.current = false;
    }
  }, [navigate, insertDownload, authorization.project, search]);

  useEffect(() => {
    if (!authorization.project) {
      return;
    }
    const missingDatasets = search.datasets.filter(
      (datasetUri) =>
        !authorization.project!.project_dataset.some(
          (d) => d.dataset_uri === datasetUri,
        ),
    );
    if (missingDatasets.length > 0) {
      authorization.setAddedDatasets(missingDatasets);
    }
    // it tries adding the full authorization object as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search.datasets,
    startDownload,
    authorization.project,
    authorization.setAddedDatasets,
  ]);

  const onConfirm = useCallback(async () => {
    if (!authorization.project?.identifier) {
      return;
    }
    await authorization.insertAdded();
    await startDownload();
  }, [authorization, startDownload]);

  const { t } = useLingui();

  return (
    <AuthorizationContext.Provider value={authorization}>
      <Grid>
        <Grid.Col span={12}>
          <Stack>
            <p style={{ textAlign: "center", marginBottom: 0 }}>
              <Trans>You are downloading data on behalf of</Trans>
            </p>
            <strong style={{ textAlign: "center" }}>
              {authorization.project?.identifier} {authorization.project?.name}
            </strong>
          </Stack>
        </Grid.Col>
        <Grid.Col span={4}>
          <h3>
            1. <Trans>Datasets</Trans>
          </h3>
          <AuthorizationDatasetSelector hideSelection />
        </Grid.Col>
        <Grid.Col span={4}>
          <h3>
            2. <Trans>Responsibilities</Trans>
          </h3>
          <AuthorizationResponsibilityBoxes />
        </Grid.Col>
        <Grid.Col span={4}>
          <h3>
            3. <Trans>Costs</Trans>
          </h3>
          <Stack gap="lg">
            <p>
              <Trans>
                Currently, during the testing phase, there are no costs
                associated with using this service
              </Trans>
            </p>
          </Stack>
        </Grid.Col>
        <Grid.Col span={12}>
          <AuthorizationConfirmButton
            onConfirm={onConfirm}
            text={t`Download`}
          />
          {submitError && <ErrorAlert>{submitError}</ErrorAlert>}
        </Grid.Col>
      </Grid>
    </AuthorizationContext.Provider>
  );
}
